import React, { useContext, useEffect, useRef, useState } from 'react';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { ApiContext } from '@api/api';
import { LovCompanyLocationWithOpeningHours, CompanyModuleEnum } from '@api/ringil-api.v2';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import { deferStampDataTestIdOnDescendantRef, toTestIdSlug } from '@utils/testUtils';
import { isNotNullOrUndefined } from '@utils/utils';
import useTranslationLgs from '@hooks/i18n/useTranslation';

interface ComponentProps {
  companyId: string;
  companyLocationId: string;
  onChange: (companyLocation: LovCompanyLocationWithOpeningHours) => void;
  style?: any;
  moduleName: CompanyModuleEnum;
  disabled?: boolean;
  showOnlyWithRamp?: boolean;
  filterPartnerLocation?: boolean;
  filterEnabled?: boolean;
  // Opt-in (LocationCard): when the LOV returns exactly one location and none is selected yet, preselect it.
  autoSelectSingle?: boolean;
  dataTestId?: string;
}

const InputCompanyLocation: React.FC<ComponentProps> = ({
                                                          companyLocationId,
                                                          companyId,
                                                          onChange,
                                                          moduleName,
                                                          style,
                                                          disabled = false,
                                                          showOnlyWithRamp = false,
                                                          filterPartnerLocation = false,
                                                          filterEnabled = false,
                                                          autoSelectSingle = false,
                                                          dataTestId,
                                                        }) => {
  const { tr } = useTranslationLgs();
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const [options, setOptions] = useState<LovCompanyLocationWithOpeningHours[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<LovCompanyLocationWithOpeningHours | null>(null);
  const { lovGetAllLocationsForCompanyId } = useContext(ApiContext);
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return deferStampDataTestIdOnDescendantRef(wrapperRef, '.p-dropdown', dataTestId);
  }, [dataTestId, options]);

  // RD-2890: a record being edited can point at a location that has since been archived (superseded).
  // Such a location is filtered out of the LOV, so the picker used to end up with a preselected id that
  // is not in the list — it silently dropped the value (and crashed on the way there). The backend pins
  // one id into the response via `includeLocationId`, bypassing the offer filters only. The id is latched
  // on the first render that carries one so that picking a different location does not refetch the list.
  const pinnedLocationIdRef = useRef<string | undefined>(undefined);
  if (pinnedLocationIdRef.current === undefined && isNotNullOrUndefined(companyLocationId)) {
    pinnedLocationIdRef.current = companyLocationId;
  }
  const pinnedLocationId = pinnedLocationIdRef.current;

  useEffect(() => {
    if (companyId) {
      lovGetAllLocationsForCompanyId('', showOnlyWithRamp, filterPartnerLocation, pinnedLocationId, moduleName, companyId, d => {
        setOptions(d);
        if (companyLocationId) {
          const found = d.find(t => t.id === companyLocationId);
          if (found == null) {
            setSelectedLocation(null);
            onChange(null);
          } else {
            setSelectedLocation(found);
          }
        } else if (autoSelectSingle && d.length === 1) {
          setSelectedLocation(d[0]);
          onChange(d[0]);
        }
      });
    }
  }, [companyId, pinnedLocationId]);

  // An archived (superseded) location is kept as a valid value but marked, so that it is obvious it is
  // not a location one would newly pick. Every other entry the LOV returns has isDeleted === false.
  const renderLocation = (option: LovCompanyLocationWithOpeningHours, testId?: string) => (
    <span className={option?.isDeleted ? 'text-content-tertiary' : undefined} data-testid={testId}>
      {option?.name}
      {option?.isDeleted && ` (${tr('InputCompanyLocation.archived', 'archived')})`}
    </span>
  );

  const itemTemplate = (option: LovCompanyLocationWithOpeningHours) =>
    renderLocation(option, dataTestId ? `${dataTestId}-${toTestIdSlug(option?.name || '')}` : undefined);

  // PrimeReact hides — but still renders — its own 'empty' filler to keep the closed dropdown's height
  // when nothing is selected; a value template must do the same or the control collapses.
  const valueTemplate = (option: LovCompanyLocationWithOpeningHours) => (option ? renderLocation(option) : 'empty');

  return (
    <>
      {loggedUser && (
        <div ref={wrapperRef} style={{ width: '100%' }}>
          <Dropdown
            style={ style }
            value={{ id: companyLocationId }}
            options={options}
            optionLabel={'name'}
            dataKey={'id'}
            onChange={e => {
              setSelectedLocation(e.value);
              onChange(e.value);
            }}
            disabled={disabled}
            filter={filterEnabled}
            itemTemplate={itemTemplate}
            valueTemplate={valueTemplate}
          />
          <COTimezone dataTestId={dataTestId ? `${dataTestId}-timezone` : undefined} timezoneId={selectedLocation?.timezoneId} />
        </div>
      )}
    </>
  );
};

export default InputCompanyLocation;
